import { Button, Card, Classes, H5, Icon, InputGroup, Intent } from '@blueprintjs/core'
import { TEXT_MUTED } from '@blueprintjs/core/lib/esm/common/classes'
import { Tooltip2 } from '@blueprintjs/popover2'
import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useApi } from '../contexts/ApiProvider'
import { useUser } from '../contexts/UserProvider'
import { useWindow } from '../contexts/WindowProvider'
import AugMdDesignerDialog from './AugMdDesignerDialog'
import CreateAxesDialog from './CreateAxesDialog'
import LoadingSpinner from './LoadingSpinner'
import NewConfirmitSurveyDialog, {
  ConfirmitSurveyStatsDialog,
} from './NewConfirmitSurveyDialog'
import './Surveys.css'
import TimeAgo from './TimeAgo'
import { AppToaster } from './toaster'
import { errorObjectToString } from '../ApiClient'

/**
 * Component to display all survey cards
 * @returns {JSX.Element} JSX Element
 */
export default function Surveys() {
  const {
    surveys,
    surveysPagination,
    surveysSearch,
    setSurveysSearch,
    fetchSurveys,
    settings: { surveysView },
  } = useWindow()
  const [searchInput, setSearchInput] = useState(surveysSearch)

  const { user } = useUser()
  const isAnalyst = user.role === 'analyst'
  const isResearcher = user.role === 'researcher'
  const isAnalystOrResearcher = isAnalyst || isResearcher

  const handlePageChange = (newPage) => {
    fetchSurveys(newPage, surveysPagination.per_page, surveysSearch)
  }

  const handleSearch = () => {
    setSurveysSearch(searchInput)
    fetchSurveys(1, surveysPagination.per_page, searchInput)
  }

  const handleClearSearch = () => {
    setSearchInput('')
    setSurveysSearch('')
    fetchSurveys(1, surveysPagination.per_page, '')
  }

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch()
    }
  }

  return (
    <div className="surveys-container">
      {surveys === undefined ? (
        <LoadingSpinner />
      ) : surveys === null ? (
        <p>Couldn't retrieve surveys</p>
      ) : (
        <>
          <div style={{ paddingTop: '10px', paddingLeft: '15px', paddingRight: '10px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <InputGroup
              placeholder="Search surveys by name"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyPress={handleKeyPress}
              rightElement={
                searchInput ? (
                  <Button
                    icon="cross"
                    minimal
                    onClick={handleClearSearch}
                  />
                ) : null
              }
              style={{ width: '300px', height: '30px' }}
            />
            <Button
              icon="search"
              intent="primary"
              onClick={handleSearch}
              style={{ height: '30px' }}
            >
              Search
            </Button>
          </div>
          <AugMdDesignerDialog />
          <NewConfirmitSurveyDialog />
          <CreateAxesDialog />
          <ConfirmitSurveyStatsDialog />
          {surveysView === 'table' ? (
            <>
              <table
                className={[
                  Classes.HTML_TABLE,
                  Classes.HTML_TABLE_BORDERED,
                  Classes.HTML_TABLE_CONDENSED,
                  'sticky-header',
                ].join(' ')}
                style={{ width: '100%', marginBottom: 15, marginRight: 15, marginLeft: 15 }}
                id="surveys-table"
              >
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Quick Links</th>
                    <th className="align-right">Respondents</th>
                    <th className="align-right">Variables</th>
                    <th className="align-right">Created time</th>
                    <th>Created by</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {surveys.map(survey => {
                    const showConfirmitButtons =
                      isAnalystOrResearcher &&
                      survey?.confirmit_id !== null &&
                      survey?.confirmit_id !== undefined
                    return (
                      <tr key={survey.id}>
                        <td>
                          <Link to={`/survey/${survey.id}`}>{survey.name}</Link>
                        </td>
                        <td>
                          <Tooltip2 content="Data" position="top">
                            <a href={`/survey/${survey.id}/explorer`}>
                              <Button
                                icon={<Icon icon="numerical" />}
                                minimal
                                small
                              />
                            </a>
                          </Tooltip2>
                          <Tooltip2 content="Variables" position="top">
                            <a href={`/survey/${survey.id}/variables`}>
                              <Button
                                icon={<Icon icon="variable" />}
                                minimal
                                small
                              />
                            </a>
                          </Tooltip2>
                          <Tooltip2 content="Viz" position="top">
                            <a href={`/survey/${survey.id}/viz?tableType=index`}>
                              <Button
                                icon={<Icon icon="panel-table" />}
                                minimal
                                small
                              />
                            </a>
                          </Tooltip2>
                        </td>
                        <td className="align-right">
                          {survey.n?.toLocaleString()}
                        </td>
                        <td className="align-right">
                          {survey.num_variables?.toLocaleString()}
                        </td>
                        <td className="align-right">
                          {<TimeAgo isoDate={survey.created_on} />}
                        </td>
                        <td>{survey.created_by.email.split('@')[0]}</td>
                        <td className="actions-cell">
                          <span>
                            {showConfirmitButtons && (
                              <>
                                <ConfirmitSyncButton survey={survey} />
                                <ConfirmitStatsButton
                                  confirmitSurveyId={survey.confirmit_id}
                                />
                              </>
                            )}
                            {isAnalystOrResearcher && (
                              <>
                                <RenameSurveyButton surveyId={survey.id} />
                                <DuplicateSurveyButton surveyId={survey.id} />
                                <DeleteSurveyButton surveyId={survey.id} />
                              </>
                            )}
                          </span>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
              <PaginationControls 
                pagination={surveysPagination} 
                onPageChange={handlePageChange} 
              />
            </>
          ) : (
            <>
              <div className="flex-grid" style={{ padding: '32px', gap: '24px' }}>
                {isAnalystOrResearcher && <NewSurveyCard />}
                {surveys.map(survey => (
                  <SurveyCard key={survey.id} survey={survey} />
                ))}
              </div>
              <PaginationControls 
                pagination={surveysPagination} 
                onPageChange={handlePageChange} 
              />
            </>
          )}
        </>
      )}
    </div>
  )
}

/**
 * Empty card with a plus icon to create a new survey
 * @returns {JSX.Element} JSX Element
 */
function NewSurveyCard() {
  const navigate = useNavigate()
  return (
    <Card
      interactive
      className="new-survey-card"
      onClick={() => navigate('/new')}
    >
      <Tooltip2 content="New Survey" minimal>
        <Link to="/new">
          <Icon size={36} icon="plus" intent={Intent.NONE} />
        </Link>
      </Tooltip2>
    </Card>
  )
}

/**
 * Card to display a survey
 * @param {Object} survey survey object from the API
 * @returns {JSX.Element} JSX Element
 */
function SurveyCard({ survey }) {
  const { user } = useUser()
  const isAnalyst = user.role === 'analyst'
  const isResearcher = user.role === 'researcher'
  const isAnalystOrResearcher = isAnalyst || isResearcher

  const showConfirmitButtons =
    isAnalystOrResearcher &&
    survey?.confirmit_id !== null &&
    survey?.confirmit_id !== undefined

  const navigate = useNavigate()

  return (
    <Card
      key={survey.id}
      interactive
      className="survey-card"
      onClick={() => navigate('/survey/' + survey.id)}
    >
      <H5 className={Classes.TEXT_OVERFLOW_ELLIPSIS}>
        <Link to={'/survey/' + survey.id}>{survey.name}</Link>
      </H5>
      <div className={TEXT_MUTED}>
        {survey.has_data === true ? (
          <ul>
            <li>{survey.n} respondents</li>
            <li>{survey.num_variables} variables</li>
          </ul>
        ) : null}
        <p>
          {survey?.created_on && (
            <>
              created <TimeAgo isoDate={survey.created_on} />
            </>
          )}
          {survey?.created_by?.email ? (
            <span style={{ padding: 0, display: 'block' }}>{`by ${
              survey.created_by.email.split('@')[0]
            }`}</span>
          ) : null}
        </p>
      </div>
      <div className="icon-group">
        {showConfirmitButtons && (
          <>
            <ConfirmitSyncButton survey={survey} />
            <ConfirmitStatsButton confirmitSurveyId={survey.confirmit_id} />
          </>
        )}
        {isAnalystOrResearcher && (
          <>
            <RenameSurveyButton surveyId={survey.id} />
            <DuplicateSurveyButton surveyId={survey.id} />
            <DeleteSurveyButton surveyId={survey.id} />
          </>
        )}
      </div>
    </Card>
  )
}

/**
 * Button to display the Confirmit survey stats dialog
 * @param {number} confirmitSurveyId Confirmit survey ID
 * @returns {JSX.Element} JSX Element
 */

function ConfirmitStatsButton({ confirmitSurveyId }) {
  const { setDialogOpen } = useWindow()
  return (
    <div className="survey-icon">
      <Tooltip2 content="Confirmit stats">
        <Button
          icon="cloud"
          intent="primary"
          small
          minimal
          onClick={ev => {
            ev.stopPropagation()
            setDialogOpen('ConfirmitSurveyStatsDialog - ' + confirmitSurveyId)
          }}
        />
      </Tooltip2>
    </div>
  )
}

/**
 * Button to sync a survey with Confirmit
 * @param {number} surveyId survey ID
 * @returns {JSX.Element} JSX Element
 */
function ConfirmitSyncButton({ survey }) {
  const [isSyncing, setIsSyncing] = useState(false)
  const { setSurveys, fetchSurveys, surveysPagination, surveysSearch } = useWindow()
  const api = useApi()

  const sync_confirmit = async () => {
    setIsSyncing(true)
    const response = await api.post('/confirmit/sync', {
      id: survey.id,
    })
    if (response.ok) {
      const {
        n_new_responses: nNewResponses,
        n_updated_responses: nUpdatedResponses,
        survey: newSurvey,
      } = response.body

      // Refresh current page to show updated survey data
      fetchSurveys(surveysPagination.page, surveysPagination.per_page, surveysSearch)

      let message = 'Survey synced.'
      if (nNewResponses === 1) {
        message += ' 1 new response.'
      } else if (nNewResponses > 1) {
        message += ` ${nNewResponses} new responses.`
      }
      if (nUpdatedResponses === 1) {
        message += ' 1 response updated.'
      } else if (nUpdatedResponses > 1) {
        message += ` ${nUpdatedResponses} responses updated.`
      }

      if (nNewResponses === 0 && nUpdatedResponses === 0) {
        message += ' No new or updated responses.'
      }

      AppToaster.show({
        message: message,
        intent: 'success',
        icon: 'tick',
      })
    } else {
      AppToaster.show({
        message: 'Survey sync failed',
        intent: 'danger',
        icon: 'error',
      })
    }
    setIsSyncing(false)
  }

  const lastSynced = <TimeAgo isoDate={survey.last_synced} />
  const content = (
    <div>
      <p>Sync with Confirmit</p>
      <p>Last synced: {lastSynced}</p>
    </div>
  )

  return (
    <div className="survey-icon">
      <Tooltip2 content={content}>
        <Button
          icon="refresh"
          intent="primary"
          small
          minimal
          loading={isSyncing}
          onClick={ev => {
            ev.stopPropagation()
            sync_confirmit()
          }}
        />
      </Tooltip2>
    </div>
  )
}

/**
 * Button to delete a survey
 * @param {number} surveyId survey ID
 * @returns {JSX.Element} JSX Element
 */
function DeleteSurveyButton({ surveyId }) {
  const api = useApi()
  const { fetchSurveys, surveysPagination, surveysSearch, loading, setLoading } = useWindow()

  const handleDeleteSurvey = async (ev, surveyId) => {
    ev.stopPropagation()
    const deleteConfirmed = window.confirm(
      'Are you sure you want to delete this survey? This cannot be undone.'
    )
    if (!deleteConfirmed) return
    setLoading(surveyId)
    const response = await api.delete('/survey/' + surveyId)
    setLoading(false)
    if (response.ok) {
      // Refresh current page after deletion
      fetchSurveys(surveysPagination.page, surveysPagination.per_page, surveysSearch)
      AppToaster.show({
        message: 'Survey successfully deleted',
        intent: 'success',
        icon: 'tick',
      })
    } else {
      if (response.status === 404) {
        alert('Survey not found. It may have already been deleted.')
      } else if (response.status === 403) {
        alert("Not allowed to delete other user's surveys")
      }
    }
  }

  return (
    <div className="survey-icon">
      <Tooltip2 content="Delete survey">
        <Button
          icon="trash"
          intent="danger"
          small
          loading={loading === surveyId}
          minimal
          onClick={ev => handleDeleteSurvey(ev, surveyId)}
        />
      </Tooltip2>
    </div>
  )
}

function DuplicateSurveyButton({ surveyId }) {
  const api = useApi()
  const { fetchSurveys, surveysPagination, surveysSearch, loading, setLoading } = useWindow()

  const handleDuplicateSurvey = async (ev, surveyId) => {
    ev.stopPropagation()
    const dupeConfirmed = window.confirm(
      'Are you sure you want to duplicate this survey?'
    )
    if (!dupeConfirmed) return
    setLoading(`${surveyId}-dupe`)
    const response = await api.get('/survey/' + surveyId + '/duplicate')
    setLoading(false)
    if (response.ok) {
      // Refresh current page to show the duplicated survey
      fetchSurveys(surveysPagination.page, surveysPagination.per_page, surveysSearch)
      AppToaster.show({
        message: 'Survey successfully duplicated.',
        intent: 'success',
        icon: 'tick',
      })
    } else {
      const message = errorObjectToString(response.body.messages.json)
      AppToaster.show({
        message,
        intent: 'danger',
        icon: 'error',
      })
    }
  }

  return (
    <div className="survey-icon">
      <Tooltip2 content="Duplicate survey">
        <Button
          icon="duplicate"
          small
          loading={loading === `${surveyId}-dupe`}
          minimal
          onClick={ev => handleDuplicateSurvey(ev, surveyId)}
        />
      </Tooltip2>
    </div>
  )
}

function RenameSurveyButton({ surveyId }) {
  const api = useApi()
  const { fetchSurveys, surveysPagination, surveysSearch, loading, setLoading } = useWindow()

  const handleRenameSurvey = async (ev, surveyId) => {
    ev.stopPropagation()
    const newName = prompt('New Survey Name')
    if (!newName) return
    setLoading(`${surveyId}-rename`)
    const response = await api.put(`/survey`, {
      id: surveyId,
      name: newName,
    })
    setLoading(false)
    if (response.ok) {
      // Refresh current page to show renamed survey
      fetchSurveys(surveysPagination.page, surveysPagination.per_page, surveysSearch)
      AppToaster.show({
        message: 'Survey renamed',
        intent: 'success',
        icon: 'tick-circle',
      })
    } else {
      const message = errorObjectToString(response.body.messages.json)
      AppToaster.show({
        message,
        intent: 'danger',
        icon: 'error',
      })
    }
    setLoading(false)
  }

  return (
    <div className="survey-icon">
      <Tooltip2 content="Rename survey">
        <Button
          icon="edit"
          // intent="danger"
          small
          loading={loading === `${surveyId}-rename`}
          minimal
          onClick={ev => handleRenameSurvey(ev, surveyId)}
        />
      </Tooltip2>
    </div>
  )
}

/**
 * Pagination controls component
 * @param {Object} pagination - pagination metadata
 * @param {Function} onPageChange - callback for page changes
 * @returns {JSX.Element} JSX Element
 */
function PaginationControls({ pagination, onPageChange }) {
  const { page, total_pages, total_items, per_page } = pagination

  if (total_pages <= 1) return null

  const startItem = (page - 1) * per_page + 1
  const endItem = Math.min(page * per_page, total_items)

  return (
    <div style={{ 
      display: 'flex', 
      justifyContent: 'space-between', 
      alignItems: 'center', 
      padding: '16px',
      borderTop: '1px solid #e1e8ed'
    }}>
      <div className={Classes.TEXT_MUTED}>
        Showing {startItem}-{endItem} of {total_items} surveys
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Button
          icon="chevron-left"
          minimal
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
        />
        <span style={{ padding: '0 16px' }}>
          Page {page} of {total_pages}
        </span>
        <Button
          icon="chevron-right"
          minimal
          disabled={page >= total_pages}
          onClick={() => onPageChange(page + 1)}
        />
      </div>
    </div>
  )
}
